import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useMemo } from "react";

interface HealthEvent {
  event_type: string;
  relative_time: string;
}

interface FeedingSummary {
  primary_prey: string;
  typical_interval: string;
  last_fed: string | null;
}

interface PublicDetail {
  health_history: HealthEvent[];
  feeding_summary: FeedingSummary;
}

interface PublicCharts {
  weight: Array<{
    label: string;
    weight_g: number;
  }> | null;
  feeding: Array<{
    period: string;
    count: number;
  }> | null;
}

interface PublicAnimal {
  id: string;
  species: string;
  common_name: string | null;
  identifier: string | null;
  morph: string | null;
  sex: string | null;
  photo_url: string | null;
  public_health_status: string | null;
  public_detail: PublicDetail | null;
  public_charts: PublicCharts | null;
}

interface VisibilityFlags {
  show_animal_images: boolean;
  show_animal_morph: boolean;
  show_animal_sex: boolean;
  show_health_status: boolean;
  show_weight_chart: boolean;
  show_feeding_chart: boolean;
}

interface PublicAnimalDetailModalProps {
  animal: PublicAnimal | null;
  isOpen: boolean;
  onClose: () => void;
  visibilityFlags: VisibilityFlags;
}

export function PublicAnimalDetailModal({ 
  animal, 
  isOpen, 
  onClose, 
  visibilityFlags 
}: PublicAnimalDetailModalProps) {
  if (!animal) return null;

  const healthStatus = animal.public_health_status;
  const detail = animal.public_detail;
  const weightData = animal.public_charts?.weight || [];
  const feedingData = animal.public_charts?.feeding || [];
  const showWeightChart = visibilityFlags.show_weight_chart && weightData.length >= 2;
  const showFeedingChart = visibilityFlags.show_feeding_chart && feedingData.length >= 1;

  // Generate accessible text summaries for charts
  const weightSummary = useMemo(() => {
    if (!showWeightChart || weightData.length < 2) return null;
    const first = weightData[0];
    const last = weightData[weightData.length - 1];
    const trend = last.weight_g > first.weight_g ? 'increased' : 
                  last.weight_g < first.weight_g ? 'decreased' : 'remained stable';
    return `Weight ${trend} from ${first.weight_g}g to ${last.weight_g}g over ${weightData.length} recorded measurements.`;
  }, [showWeightChart, weightData]);

  const feedingSummary = useMemo(() => {
    if (!showFeedingChart || feedingData.length === 0) return null;
    const totalFeedings = feedingData.reduce((sum, item) => sum + (item.count || 0), 0);
    return `Total of ${totalFeedings} feeding${totalFeedings !== 1 ? 's' : ''} recorded across ${feedingData.length} time period${feedingData.length !== 1 ? 's' : ''}.`;
  }, [showFeedingChart, feedingData]);
  
  const statusColor = healthStatus === 'Healthy' 
    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    : healthStatus === 'Recovering'
    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
    : healthStatus === 'Under Vet Review'
    ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
    : 'bg-muted text-muted-foreground';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        aria-labelledby="animal-detail-title"
        aria-describedby="animal-detail-description"
        role="dialog"
      >
        <DialogHeader>
          <DialogTitle id="animal-detail-title" className="flex items-center justify-between pr-8">
            <div className="flex flex-col gap-1">
              <span className="text-xl font-semibold">
                {animal.common_name || animal.species}
              </span>
              {animal.common_name && (
                <span className="text-sm text-muted-foreground font-normal">
                  {animal.species}
                </span>
              )}
            </div>
            {visibilityFlags.show_health_status && healthStatus && (
              <Badge className={statusColor}>
                {healthStatus}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Image */}
          {visibilityFlags.show_animal_images && animal.photo_url && (
            <div className="flex justify-center">
              <img
                src={animal.photo_url}
                alt={`${animal.common_name || animal.species}${animal.morph && visibilityFlags.show_animal_morph ? `, ${animal.morph} morph` : ''}`}
                className="max-h-64 rounded-lg object-cover"
              />
            </div>
          )}

          {/* Basic Details */}
          <div id="animal-detail-description">
            <h3 className="text-lg font-semibold mb-3">Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-muted-foreground">Species:</span>
                <div className="mt-1">{animal.species}</div>
              </div>
              
              {animal.common_name && (
                <div>
                  <span className="font-medium text-muted-foreground">Name:</span>
                  <div className="mt-1">{animal.common_name}</div>
                </div>
              )}
              
              {visibilityFlags.show_animal_sex && animal.sex && (
                <div>
                  <span className="font-medium text-muted-foreground">Sex:</span>
                  <div className="mt-1">
                    <Badge variant="outline">{animal.sex}</Badge>
                  </div>
                </div>
              )}
              
              {visibilityFlags.show_animal_morph && animal.morph && (
                <div>
                  <span className="font-medium text-muted-foreground">Morph:</span>
                  <div className="mt-1">
                    <Badge variant="outline">{animal.morph}</Badge>
                  </div>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Health History */}
          {visibilityFlags.show_health_status && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Recent Health Events</h3>
              {detail?.health_history && detail.health_history.length > 0 ? (
                <div className="space-y-2">
                  {detail.health_history.map((event, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-2 text-sm text-muted-foreground"
                    >
                      <span className="font-medium text-foreground">{event.event_type}</span>
                      <span>•</span>
                      <span>{event.relative_time}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No recent health events recorded.
                </p>
              )}
            </div>
          )}

          {/* Feeding Summary */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Feeding Summary</h3>
            {detail?.feeding_summary ? (
              <div className="space-y-2 text-sm">
                {detail.feeding_summary.primary_prey !== 'Not enough data' && (
                  <div className="flex items-start gap-2">
                    <span className="font-medium text-muted-foreground min-w-[120px]">
                      Primary prey:
                    </span>
                    <span>{detail.feeding_summary.primary_prey}</span>
                  </div>
                )}
                
                {detail.feeding_summary.typical_interval && detail.feeding_summary.typical_interval !== 'Insufficient data' && (
                  <div className="flex items-start gap-2">
                    <span className="font-medium text-muted-foreground min-w-[120px]">
                      Typical interval:
                    </span>
                    <span>{detail.feeding_summary.typical_interval}</span>
                  </div>
                )}
                
                {detail.feeding_summary.last_fed && detail.feeding_summary.last_fed !== 'Unknown' && (
                  <div className="flex items-start gap-2">
                    <span className="font-medium text-muted-foreground min-w-[120px]">
                      Last fed:
                    </span>
                    <span>{detail.feeding_summary.last_fed}</span>
                  </div>
                )}
                
                {detail.feeding_summary.primary_prey === 'Not enough data' && 
                 (!detail.feeding_summary.last_fed || detail.feeding_summary.last_fed === 'Unknown') && (
                  <p className="text-muted-foreground">Feeding data not available.</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Feeding data not available.
              </p>
            )}
          </div>

          {/* Charts Section */}
          {(showWeightChart || showFeedingChart) && (
            <>
              <Separator />
              <div className="space-y-6">
                {/* Weight Chart */}
                {showWeightChart && (
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-semibold">Weight trend</h4>
                      <p className="text-xs text-muted-foreground">
                        Approximate trend based on recent weights
                      </p>
                    </div>
                    <div 
                      className="h-64 w-full" 
                      role="img" 
                      aria-label={`Weight trend chart for ${animal.common_name || animal.species}`}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={weightData}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis 
                            dataKey="label" 
                            tick={{ fontSize: 12 }}
                            className="text-muted-foreground"
                          />
                          <YAxis 
                            tick={{ fontSize: 12 }}
                            className="text-muted-foreground"
                            label={{ value: 'Weight (g)', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--background))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '0.5rem'
                            }}
                            labelStyle={{ color: 'hsl(var(--foreground))' }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="weight_g" 
                            stroke="hsl(var(--primary))" 
                            strokeWidth={2}
                            dot={{ fill: 'hsl(var(--primary))' }}
                            name="Weight (g)"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    {weightSummary && (
                      <p className="text-xs text-muted-foreground" aria-live="polite">
                        {weightSummary}
                      </p>
                    )}
                  </div>
                )}

                {/* Feeding Chart */}
                {showFeedingChart && (
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-semibold">Feeding history</h4>
                      <p className="text-xs text-muted-foreground">
                        Showing recent feeding activity by time window
                      </p>
                    </div>
                    <div 
                      className="h-64 w-full" 
                      role="img" 
                      aria-label={`Feeding history chart for ${animal.common_name || animal.species}`}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={feedingData}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis 
                            dataKey="period" 
                            tick={{ fontSize: 12 }}
                            className="text-muted-foreground"
                          />
                          <YAxis 
                            tick={{ fontSize: 12 }}
                            className="text-muted-foreground"
                            label={{ value: 'Feedings', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--background))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '0.5rem'
                            }}
                            labelStyle={{ color: 'hsl(var(--foreground))' }}
                          />
                          <Bar 
                            dataKey="count" 
                            fill="hsl(var(--primary))" 
                            radius={[4, 4, 0, 0]}
                            name="Feed Count"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    {feedingSummary && (
                      <p className="text-xs text-muted-foreground" aria-live="polite">
                        {feedingSummary}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Footer */}
          <div className="flex justify-end pt-4">
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
